import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Estrategia de preloading selectiva
 * Solo precarga rutas marcadas con `data: { preload: true }`
 *
 * Basado en las mejores prácticas de Angular routing:
 * - Rutas críticas se precargan inmediatamente
 * - Rutas secundarias se precargan después de un delay
 * - Rutas de administración solo se precargan para usuarios admin
 */
@Injectable({
  providedIn: 'root',
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  constructor(private authService: AuthService) {}
  /**
   * Determina si una ruta debe ser precargada
   * @param route - La ruta a evaluar
   * @param load - Función para cargar el módulo
   * @returns Observable que resuelve cuando se completa la carga
   */
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Si la ruta tiene data.preload = true, precargar inmediatamente
    if (route.data && route.data['preload'] === true) {
      console.log('Preloading route:', route.path);
      return load();
    }

    // Si la ruta tiene data.preload = 'delayed', precargar después de un delay
    if (route.data && route.data['preload'] === 'delayed') {
      console.log('Delayed preloading route:', route.path);
      return this.delayedPreload(load, 2000); // 2 segundos de delay
    }

    // Si la ruta tiene data.preload = 'admin', verificar permisos antes de precargar
    if (route.data && route.data['preload'] === 'admin') {
      return this.adminPreload(route, load);
    }

    // No precargar rutas sin configuración específica
    return of(null);
  }

  /**
   * Precarga con delay para rutas secundarias
   */
  private delayedPreload(
    load: () => Observable<any>,
    delay: number
  ): Observable<any> {
    return new Observable(observer => {
      setTimeout(() => {
        load().subscribe({
          next: value => observer.next(value),
          error: error => observer.error(error),
          complete: () => observer.complete(),
        });
      }, delay);
    });
  }

  /**
   * Precarga condicional para rutas de administración
   */
  private adminPreload(
    route: Route,
    load: () => Observable<any>
  ): Observable<any> {
    // Verificar si el usuario es admin usando AuthService
    const isAdmin = this.checkAdminStatus();

    if (isAdmin) {
      console.log('Admin preloading route:', route.path);
      return load();
    }

    console.log('Skipping admin route preload:', route.path);
    return of(null);
  }

  /**
   * Verifica si el usuario actual es administrador
   */
  private checkAdminStatus(): boolean {
    // Reutilizar la lógica centralizada de AuthService
    return this.authService.isAdmin();
  }
}
